// Scalars every DTO in the contract is built from.
//
// Spec: spec/README.md "Conventions", spec/08-api-design.md §2.3, spec/06-billing-pricing-and-payments.md §1.
import { z } from "../zod.js";
/**
 * A moment on the wire: RFC 3339 in UTC, always with a `Z`. Offsets are rejected so that two
 * services never disagree about what "the same instant" looks like in a log or an idempotency key.
 * The venue's timezone decides business days and opening hours; it never appears in a timestamp.
 */
export const IsoDateTimeSchema = z.iso
    .datetime()
    .openapi('IsoDateTime', { description: 'RFC 3339 timestamp in UTC.', example: '2026-09-20T12:00:00.120Z' });
/** A calendar date in the venue's timezone (`YYYY-MM-DD`): business days, report ranges, holidays. */
export const LocalDateSchema = z.iso
    .date()
    .openapi('LocalDate', { description: "A date in the venue's timezone.", example: '2026-09-20' });
/** A time of day in the venue's timezone, `HH:mm` on a 24-hour clock. */
export const LocalTimeSchema = z
    .string()
    .regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/, 'Expected a time of day as HH:mm')
    .openapi('LocalTime', { description: "A 24-hour time of day in the venue's timezone.", example: '18:30' });
/**
 * An IANA timezone name, as a venue carries it — validated **strictly** (spec/03 §7.1, F-ORG-01).
 *
 * A venue's timezone decides every business day, opening hour and time-of-day price in the
 * product, so "whatever `Intl` will accept" is not good enough. `Intl.DateTimeFormat` accepts
 * `asia/kolkata`, `US/Eastern`, `EST5EDT` and `GMT` without complaint, and a venue stored with one
 * of those is a name that round-trips differently through the C# station's `TimeZoneInfo`, a Linux
 * tz database lookup (case-sensitive) and Postgres. So a name is accepted only when it is spelled
 * exactly as the IANA database spells a **place**:
 *
 *   - a canonical zone from `Intl.supportedValuesOf('timeZone')`, compared case-sensitively; or
 *   - one of the IANA names ICU still files under an older canonical id. `Asia/Kolkata` is the one
 *     that matters most: ICU's canonical is `Asia/Calcutta`, and refusing the spelling the
 *     specification itself uses would be absurd.
 *
 * Aliases (`US/Eastern`), POSIX-style names (`EST5EDT`), `Etc/*` offsets and bare `UTC`/`GMT` are
 * refused: a venue is somewhere.
 */
export const TimezoneSchema = z
    .string()
    .min(1)
    .max(64)
    .refine(isIanaTimezone, 'Expected the IANA timezone name of a place, spelled exactly, e.g. Asia/Kolkata')
    .openapi('Timezone', { description: 'The IANA timezone name of a place.', example: 'Asia/Kolkata' });
/**
 * IANA's current names for zones that ICU still files under a legacy canonical id, so they are
 * absent from `Intl.supportedValuesOf('timeZone')` while `Intl` resolves them perfectly well. The
 * list only has to grow when ICU lags a tz database rename; an ICU that adopts a name lists it in
 * `supportedValuesOf`, and the entry here becomes redundant rather than wrong.
 */
export const IANA_NAMES_FILED_UNDER_LEGACY_IDS = [
    'Africa/Asmara',
    'America/Argentina/Buenos_Aires',
    'America/Argentina/Catamarca',
    'America/Argentina/Cordoba',
    'America/Argentina/Jujuy',
    'America/Argentina/Mendoza',
    'America/Atikokan',
    'America/Indiana/Indianapolis',
    'America/Kentucky/Louisville',
    'America/Nuuk',
    'Asia/Ho_Chi_Minh',
    'Asia/Kathmandu',
    'Asia/Kolkata',
    'Asia/Yangon',
    'Atlantic/Faroe',
    'Europe/Kyiv',
    'Pacific/Chuuk',
    'Pacific/Kanton',
    'Pacific/Pohnpei',
];
/** `Area/Location` or `Area/Region/Location`, each part starting with a capital, as IANA writes them. */
const IANA_PLACE_NAME = /^[A-Z][A-Za-z_]+(?:\/[A-Z][A-Za-z0-9_+-]*){1,2}$/;
/** Areas that are not places: fixed offsets, the System V names and the backward-compatible links. */
const NON_PLACE_AREAS = new Set(['Etc', 'SystemV', 'US', 'Canada', 'Brazil', 'Chile', 'Mexico']);
let canonicalZones;
function supportedZones() {
    if (canonicalZones !== undefined)
        return canonicalZones;
    // Missing on some engines (older Hermes). There the shape check and `Intl`'s own acceptance are
    // all that can be asked; the server, which is authoritative, always has it.
    const supportedValuesOf = Intl.supportedValuesOf;
    canonicalZones = typeof supportedValuesOf === 'function' ? new Set(supportedValuesOf('timeZone')) : null;
    return canonicalZones;
}
/** True when `value` is the IANA timezone name of a place, spelled exactly. See `TimezoneSchema`. */
export function isIanaTimezone(value) {
    if (typeof value !== 'string' || !IANA_PLACE_NAME.test(value))
        return false;
    if (NON_PLACE_AREAS.has(value.slice(0, value.indexOf('/'))))
        return false;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: value });
    }
    catch {
        return false;
    }
    const zones = supportedZones();
    if (zones === null)
        return true;
    return zones.has(value) || IANA_NAMES_FILED_UNDER_LEGACY_IDS.includes(value);
}
/**
 * A duration in whole seconds. Field names carry the unit (`graceSeconds`, `durationSeconds`);
 * a field that is not in seconds says so in its name (`claimMinutes`).
 */
export const DurationSecondsSchema = z
    .int()
    .nonnegative()
    .openapi('DurationSeconds', { description: 'A duration in whole seconds.', example: 3600 });
/** Basis points: `250` is 2.50% (spec/06 §1.3). Percentages are never floats either. */
export const BasisPointsSchema = z
    .int()
    .openapi('BasisPoints', { description: 'An integer percentage in basis points; 250 is 2.50%.', example: 250 });
/**
 * An ISO 4217 alphabetic currency code. The shape is enforced here; which codes an organisation
 * may actually trade in is an organisation setting, not a wire-level constraint, so the contract
 * does not carry a closed list that would have to be edited to open a new market (spec/03 §10).
 */
export const CurrencyCodeSchema = z
    .string()
    .regex(/^[A-Z]{3}$/, 'Expected an ISO 4217 alphabetic currency code')
    .openapi('CurrencyCode', { description: 'ISO 4217 alphabetic currency code.', example: 'INR' });
/** An ISO 3166-1 alpha-2 country code. */
export const CountryCodeSchema = z
    .string()
    .regex(/^[A-Z]{2}$/, 'Expected an ISO 3166-1 alpha-2 country code')
    .openapi('CountryCode', { description: 'ISO 3166-1 alpha-2 country code.', example: 'IN' });
/** `resource:action`, for example `sessions:end` (spec/01-glossary.md, spec/07 §6). */
export const PERMISSION_PATTERN = /^[a-z][a-z0-9_]*:[a-z][a-z0-9_]*$/;
export const PermissionSchema = z
    .string()
    .regex(PERMISSION_PATTERN, 'Expected a permission as resource:action')
    .openapi('Permission', { description: 'A permission, written resource:action.', example: 'sessions:end' });
/** The client-generated key on every create that moves money, time or orders (spec/08 §2.2). */
export const IdempotencyKeySchema = z
    .uuid()
    .openapi('IdempotencyKey', { description: 'Client-generated UUID, unique per logical create.' });
/** The days a `TimeWindow` covers. `holiday` is the venue's holiday calendar (spec/06 §4). */
export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
export const TimeWindowDaySchema = z
    .enum([...WEEKDAYS, 'holiday'])
    .openapi('TimeWindowDay', { description: "A weekday, or the venue's holiday calendar." });
//# sourceMappingURL=scalars.js.map